/**
 * The director: runs a walkthrough, one step at a time, on the real page.
 *
 * A step is a sentence and, optionally, a place — a screen to go to and a
 * thing on it to point at. The director goes there the way a person would
 * (the cursor travels to the menu link, the link is pressed), scrolls the
 * thing into view, dims everything else, moves the cursor onto it, and says
 * the sentence. Then it waits: for the voice to finish, or for a reading time
 * when she is not speaking aloud, or for the person to press Next.
 *
 * What it will not do is the rule the whole assistant keeps: she prepares and
 * explains, she does not commit. A step may ask to press something, and the
 * director presses it only when `mayPress` says it moves the person around;
 * otherwise it points and says so. A step may ask to type, and it types only
 * into a search or filter box — and puts back what was there when the
 * walkthrough ends. A walkthrough leaves no trace on the records.
 *
 * State is a small store read with `useSyncExternalStore`, so the overlay and
 * the dock see the same step without either owning it.
 */
import { mayPress, mayType, navLink, resolve, typeInto, type Target } from "./anchors";

export interface Step {
  /** What she says, and what the caption shows. */
  text: string;
  /** A short heading for the caption: the screen or the section. */
  title?: string;
  /** Go here first, through the menu. */
  route?: string;
  /** The thing to point at once there. */
  target?: Target;
  /** Keep it lit at least this long, in ms — a "where is" answer is looked for, not read. */
  hold?: number;
  /** What to light instead when the target cannot be found — the screen, rather than nothing. */
  fallback?: Target;
  /** point (default), press it if pressing it is safe, or type `value` into it if it is a search box. */
  action?: "point" | "press" | "type";
  value?: string;
  /**
   * Steps that can only be written once this one has run — the parts of a
   * screen exist after the screen is open. Inserted after this step, once.
   */
  grow?: () => Step[];
}

export interface Script {
  id: string;
  title: string;
  steps: Step[];
  /** Said in the dock when the walkthrough ends, with what could come next. */
  onEnd?: (how: "finished" | "stopped") => void;
}

export interface StageDeps {
  navigate: (route: string) => void;
  route: () => string;
  /** Speak the line; resolves when it is finished. Null when she is not speaking aloud. */
  speak: (text: string) => Promise<void> | null;
  silence: () => void;
}

export interface StageState {
  script: Script | null;
  index: number;
  paused: boolean;
  muted: boolean;
  /** What is lit. The overlay reads its box every frame, so scrolling and resizing follow it. */
  el: HTMLElement | null;
  cursor: { x: number; y: number; pressing: boolean } | null;
  /** A value she would type but may not, shown beside the field instead. */
  ghost: string | null;
  /** Said when she could not do what the step asked — "I could not find …". */
  note: string | null;
}

const IDLE: StageState = { script: null, index: 0, paused: false, muted: false, el: null, cursor: null, ghost: null, note: null };

let state: StageState = IDLE;
const listeners = new Set<() => void>();

function set(patch: Partial<StageState>) {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
}

export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function snapshot(): StageState {
  return state;
}

export function isRunning(): boolean {
  return !!state.script;
}

/* ── the run ──────────────────────────────────────────────────────────── */

class Interrupted extends Error {}

let deps: StageDeps | null = null;
let ctrl: AbortController | null = null;
let jumpTo: number | null = null;
let runId = 0;
/** Search boxes she typed into, and what they held before. */
let typed: { el: HTMLInputElement; was: string }[] = [];

const reduced = () => typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) return reject(new Interrupted());
    const t = window.setTimeout(resolve, ms);
    signal.addEventListener("abort", () => {
      window.clearTimeout(t);
      reject(new Interrupted());
    }, { once: true });
  });
}

/** How long a line takes to read, for when nobody is reading it aloud. */
export function readingTime(text: string): number {
  const words = text.trim().split(/\s+/).length;
  return Math.min(14_000, Math.max(2600, words * 330 + 900));
}

function centre(el: HTMLElement) {
  const r = el.getBoundingClientRect();
  return { x: r.left + Math.min(r.width / 2, 60), y: r.top + Math.min(r.height / 2, 22) };
}

async function moveTo(el: HTMLElement, signal: AbortSignal) {
  const c = centre(el);
  set({ cursor: { ...c, pressing: false } });
  await sleep(reduced() ? 60 : 720, signal);
}

async function pressAnim(signal: AbortSignal) {
  if (!state.cursor) return;
  set({ cursor: { ...state.cursor, pressing: true } });
  await sleep(reduced() ? 60 : 260, signal);
  set({ cursor: state.cursor && { ...state.cursor, pressing: false } });
}

const pathOf = (r: string) => (r.split("?")[0].replace(/\/+$/, "") || "/");

async function goTo(route: string, signal: AbortSignal) {
  if (!deps || pathOf(deps.route()) === pathOf(route)) return;
  const link = navLink(route) ?? (await resolve({ nav: route }, 0, signal));
  if (link) {
    link.scrollIntoView?.({ block: "nearest", behavior: reduced() ? "auto" : "smooth" });
    set({ el: link });
    await sleep(200, signal);
    await moveTo(link, signal);
    await pressAnim(signal);
  }
  deps.navigate(route);
  const until = Date.now() + 4000;
  while (pathOf(deps.route()) !== pathOf(route) && Date.now() < until) await sleep(80, signal);
  /* the screen's own data arrives after the route does */
  await sleep(650, signal);
}

async function runStep(step: Step, signal: AbortSignal) {
  set({ el: null, ghost: null, note: null });
  if (step.route) await goTo(step.route, signal);

  let el: HTMLElement | null = null;
  if (step.target) {
    el = await resolve(step.target, 2500, signal);
    if (!el && step.fallback) el = await resolve(step.fallback, 0, signal);
    if (signal.aborted) throw new Interrupted();
    if (el) {
      el.scrollIntoView?.({ block: "center", inline: "nearest", behavior: reduced() ? "auto" : "smooth" });
      await sleep(reduced() ? 50 : 420, signal);
      set({ el });
      await moveTo(el, signal);
    } else {
      set({ note: "I could not find that on this screen just now, so I am telling you instead of pointing." });
    }
  } else {
    set({ cursor: null });
  }

  if (el && step.action === "press") {
    if (mayPress(el)) {
      await pressAnim(signal);
      el.click();
      await sleep(500, signal);
    } else {
      set({ note: "That one changes a record, so it is yours to press — I am only pointing at it." });
    }
  }

  if (el && step.action === "type" && step.value) {
    if (mayType(el)) {
      if (!typed.some((x) => x.el === el)) typed.push({ el, was: el.value });
      el.focus({ preventScroll: true });
      for (let i = 1; i <= step.value.length; i++) {
        typeInto(el, step.value.slice(0, i));
        await sleep(reduced() ? 10 : 70, signal);
      }
    } else {
      set({ ghost: step.value });
    }
  }

  const spoken = !state.muted && deps ? deps.speak(step.text) : null;
  if (spoken) {
    /* a speech engine that never reports the end must not hold the tour forever */
    const cap = sleep(Math.max(20_000, readingTime(step.text) * 2), signal);
    /* and never quicker than it can be read: an engine with no voices reports the end at once */
    const min = sleep(Math.max(1400, readingTime(step.text) * 0.75, step.hold ?? 0), signal);
    /* whichever of these is not awaited must not surface as an unhandled rejection on Next */
    cap.catch(() => {});
    min.catch(() => {});
    await Promise.race([spoken, cap]);
    await min;
    await sleep(350, signal);
  } else {
    await sleep(Math.max(readingTime(step.text), step.hold ?? 0), signal);
  }
}

function restoreTyped() {
  for (const { el, was } of typed) {
    if (el.isConnected) typeInto(el, was);
  }
  typed = [];
}

/**
 * Start a walkthrough. Replaces any that is running: asking for a second tour
 * means this one, not that one after the first.
 */
export async function play(script: Script, d: StageDeps): Promise<void> {
  if (script.steps.length === 0) return;
  stop(false);
  deps = d;
  const me = ++runId;
  set({ ...IDLE, muted: state.muted, script, index: 0 });
  let i = 0;
  let how: "finished" | "stopped" = "finished";
  let steps = script.steps;
  const grown = new Set<Step>();
  const growAt = (list: Step[], at: number): Step[] => {
    const step = list[at];
    if (!step?.grow || grown.has(step)) return list;
    grown.add(step);
    const more = step.grow();
    if (!more.length) return list;
    const next = [...list.slice(0, at + 1), ...more, ...list.slice(at + 1)];
    set({ script: { ...script, steps: next } });
    return next;
  };
  while (i < steps.length) {
    if (me !== runId) return;
    set({ index: i });
    const c = new AbortController();
    ctrl = c;
    try {
      if (state.paused) await new Promise<void>((_, reject) => c.signal.addEventListener("abort", () => reject(new Interrupted())));
      await runStep(steps[i], c.signal);
      steps = growAt(steps, i);
      i += 1;
    } catch (e) {
      if (!(e instanceof Interrupted)) {
        /* a step that throws is skipped, not fatal: the next screen may be fine */
        i += 1;
        continue;
      }
      if (me !== runId) return;
      /* moving forward past a step that grows still brings in what it grows */
      if (jumpTo !== null && jumpTo > i) steps = growAt(steps, i);
      if (jumpTo === null) {
        how = "stopped";
        break;
      }
      /* Next on the last step is Finish */
      if (jumpTo >= steps.length) {
        jumpTo = null;
        break;
      }
      i = Math.max(0, jumpTo);
      jumpTo = null;
    }
  }
  if (me !== runId) return;
  finish();
  script.onEnd?.(how);
}

function finish() {
  deps?.silence();
  restoreTyped();
  ctrl = null;
  set({ ...IDLE, muted: state.muted });
}

function jump(to: number) {
  if (!state.script) return;
  jumpTo = to;
  deps?.silence();
  ctrl?.abort();
}

export function next(): void {
  if (!state.script) return;
  set({ paused: false });
  jump(state.index + 1);
}

export function back(): void {
  set({ paused: false });
  jump(Math.max(0, state.index - 1));
}

/** Pausing silences her and holds the step; resuming says it again from the top. */
export function togglePause(): void {
  if (!state.script) return;
  if (state.paused) {
    set({ paused: false });
    jump(state.index);
  } else {
    set({ paused: true });
    jump(state.index);
  }
}

export function toggleMute(): void {
  const muted = !state.muted;
  set({ muted });
  if (muted) deps?.silence();
}

/** Stop the walkthrough. `report` false when another is replacing it. */
export function stop(report = true): void {
  if (!state.script) return;
  const script = state.script;
  if (!report) {
    runId++;
    ctrl?.abort();
    finish();
    return;
  }
  jumpTo = null;
  deps?.silence();
  ctrl?.abort();
  if (!ctrl) {
    finish();
    script.onEnd?.("stopped");
  }
}
